import { contextEngine } from './context-engine.js';
import { format, addMinutes, isBefore, isAfter } from 'date-fns';

/**
 * Smart Notification Timing Service
 * 
 * Determines optimal times to send notifications and suggestions
 * based on user patterns, context, and preferences.
 */
class NotificationTimingService {
  constructor() {
    this.notificationQueue = new Map(); // userId -> notifications
    this.timingRules = new Map(); // userId -> timing preferences
    this.sendHistory = new Map(); // userId -> send history
    this.suppressionRules = new Map(); // userId -> suppression settings
  }

  /**
   * Schedule a notification with smart timing
   */
  scheduleNotification(userId, notification, options = {}) {
    const {
      priority = 'medium',
      category = 'general',
      respectQuietHours = true,
      maxDelay = 24 * 60, // 24 hours in minutes
      minInterval = 30 // minimum minutes between similar notifications
    } = options;

    const enhancedNotification = {
      ...notification,
      id: notification.id || this.generateNotificationId(),
      userId,
      priority,
      category,
      scheduledAt: new Date(),
      originalSendTime: notification.sendTime || new Date(),
      respectQuietHours,
      maxDelay,
      minInterval,
      attempts: 0,
      status: 'pending'
    };

    // Add to queue
    if (!this.notificationQueue.has(userId)) {
      this.notificationQueue.set(userId, []);
    }
    this.notificationQueue.get(userId).push(enhancedNotification);

    // Process queue
    this.processNotificationQueue(userId);

    return enhancedNotification.id;
  }

  /**
   * Process notification queue for a user with smart timing
   */
  async processNotificationQueue(userId) {
    const queue = this.notificationQueue.get(userId) || [];
    const pendingNotifications = queue.filter(n => n.status === 'pending');

    if (pendingNotifications.length === 0) return;

    try {
      // Get current context for timing analysis
      const context = await this.getUserContext(userId);
      const optimalTiming = await this.calculateOptimalTiming(userId, context);

      for (const notification of pendingNotifications) {
        const shouldSend = this.shouldSendNotificationNow(notification, context, optimalTiming);
        
        if (shouldSend.send) {
          await this.sendNotification(notification, shouldSend.reason);
        } else if (shouldSend.reschedule) {
          this.rescheduleNotification(notification, shouldSend.newTime, shouldSend.reason);
        } else if (shouldSend.suppress) {
          this.suppressNotification(notification, shouldSend.reason);
        }
      }
    } catch (error) {
      console.error('Error processing notification queue:', error);
    }
  }

  /**
   * Calculate optimal timing based on user patterns and context
   */
  async calculateOptimalTiming(userId, context = null) {
    if (!context) {
      context = await this.getUserContext(userId);
    }

    const { personal, temporal } = context;
    const now = new Date();

    return {
      // Current timing assessment
      currentWindow: {
        isOptimal: this.isCurrentTimeOptimal(personal, temporal),
        productivity: personal.currentProductivityScore || 0.5,
        energyLevel: personal.predictedEnergyLevel || 'normal',
        interruption: this.assessInterruptionLevel(temporal, personal),
        confidence: personal.confidenceScores?.communication || 0.5
      },

      // Preferred communication windows
      preferredWindows: this.getPreferredNotificationWindows(personal),

      // Quiet hours and restrictions
      quietHours: this.getQuietHours(userId, personal),
      
      // Pattern-based recommendations
      patterns: {
        bestResponseTimes: this.getBestResponseTimes(personal),
        avoidancePeriods: this.getAvoidancePeriods(personal),
        optimalGaps: this.getOptimalNotificationGaps(personal)
      },

      // Context-specific adjustments
      contextAdjustments: {
        familyAvailability: this.assessFamilyAvailability(context.family),
        workFocus: this.assessWorkFocusPeriod(temporal),
        energyAlignment: this.assessEnergyAlignment(personal, temporal)
      }
    };
  }

  /**
   * Determine if a notification should be sent now
   */
  shouldSendNotificationNow(notification, context, optimalTiming) {
    const now = new Date();
    const { priority, category, respectQuietHours, maxDelay, minInterval } = notification;
    
    // Check if max delay exceeded
    const timeSinceScheduled = (now - notification.scheduledAt) / (1000 * 60); // minutes
    if (timeSinceScheduled > maxDelay) {
      return { 
        send: true, 
        reason: 'max-delay-exceeded',
        urgency: 'forced'
      };
    }

    // Check quiet hours
    if (respectQuietHours && this.isInQuietHours(userId, now, optimalTiming.quietHours)) {
      const nextAvailable = this.getNextAvailableTime(userId, now, optimalTiming.quietHours);
      return {
        send: false,
        reschedule: true,
        newTime: nextAvailable,
        reason: 'quiet-hours'
      };
    }

    // Check recent notification interval
    if (this.hasRecentSimilarNotification(userId, category, minInterval)) {
      const nextAllowed = this.getNextAllowedTime(userId, category, minInterval);
      return {
        send: false,
        reschedule: true,
        newTime: nextAllowed,
        reason: 'min-interval-not-met'
      };
    }

    // Critical priority - send immediately
    if (priority === 'critical') {
      return { 
        send: true, 
        reason: 'critical-priority',
        urgency: 'immediate'
      };
    }

    // High priority - send if not terrible timing
    if (priority === 'high') {
      const interruptionLevel = optimalTiming.currentWindow.interruption;
      if (interruptionLevel <= 0.7) { // Not too disruptive
        return { 
          send: true, 
          reason: 'high-priority-acceptable-timing',
          urgency: 'high'
        };
      } else {
        const nextGoodTime = this.findNextGoodTiming(optimalTiming, 'high');
        return {
          send: false,
          reschedule: true,
          newTime: nextGoodTime,
          reason: 'high-priority-poor-timing'
        };
      }
    }

    // Medium/Low priority - use optimal timing
    const timing = this.assessCurrentTiming(optimalTiming, category);
    
    if (timing.score >= 0.7) { // Good timing
      return { 
        send: true, 
        reason: `optimal-timing-score-${timing.score.toFixed(2)}`,
        urgency: 'normal'
      };
    } else if (timing.score >= 0.4) { // Acceptable timing
      return { 
        send: true, 
        reason: `acceptable-timing-score-${timing.score.toFixed(2)}`,
        urgency: 'low'
      };
    } else { // Poor timing - reschedule
      const nextGoodTime = this.findNextGoodTiming(optimalTiming, priority);
      return {
        send: false,
        reschedule: true,
        newTime: nextGoodTime,
        reason: `poor-timing-score-${timing.score.toFixed(2)}`
      };
    }
  }

  /**
   * Assess current timing quality for notifications
   */
  assessCurrentTiming(optimalTiming, category) {
    let score = 0.5; // Base score
    const weights = {
      productivity: 0.2,
      energy: 0.2,
      interruption: 0.3,
      patterns: 0.2,
      context: 0.1
    };

    // Productivity factor
    score += (optimalTiming.currentWindow.productivity - 0.5) * weights.productivity;

    // Energy factor
    const energyScore = this.getEnergyScore(optimalTiming.currentWindow.energyLevel);
    score += (energyScore - 0.5) * weights.energy;

    // Interruption factor (lower interruption = higher score)
    score += (1 - optimalTiming.currentWindow.interruption) * weights.interruption;

    // Pattern factor
    const patternScore = this.getPatternScore(optimalTiming.patterns, category);
    score += (patternScore - 0.5) * weights.patterns;

    // Context factor
    const contextScore = this.getContextScore(optimalTiming.contextAdjustments);
    score += (contextScore - 0.5) * weights.context;

    return {
      score: Math.max(0, Math.min(1, score)),
      factors: {
        productivity: optimalTiming.currentWindow.productivity,
        energy: energyScore,
        interruption: optimalTiming.currentWindow.interruption,
        patterns: patternScore,
        context: contextScore
      }
    };
  }

  /**
   * Send notification and track timing
   */
  async sendNotification(notification, reason) {
    const sendTime = new Date();
    
    // Update notification status
    notification.status = 'sent';
    notification.sentAt = sendTime;
    notification.sendReason = reason;
    notification.attempts++;

    // Track send history for pattern learning
    this.trackNotificationSend(notification, reason);

    // Emit notification (this would integrate with your notification system)
    this.emitNotification(notification);

    console.log(`Notification sent: ${notification.title} (${reason})`);
  }

  /**
   * Track notification send for pattern learning
   */
  trackNotificationSend(notification, reason) {
    const userId = notification.userId;
    const history = this.sendHistory.get(userId) || [];
    
    history.push({
      notificationId: notification.id,
      category: notification.category,
      priority: notification.priority,
      scheduledAt: notification.scheduledAt,
      sentAt: notification.sentAt,
      reason,
      delayMinutes: (notification.sentAt - notification.originalSendTime) / (1000 * 60)
    });

    // Keep last 100 notifications
    if (history.length > 100) {
      history.splice(0, history.length - 100);
    }

    this.sendHistory.set(userId, history);
  }

  /**
   * Get preferred notification windows based on patterns
   */
  getPreferredNotificationWindows(personal) {
    const communicationTimes = personal.preferredCommunicationTimes || [];
    const productivityPeaks = personal.productivityPeaks || [];
    
    // Convert to time windows
    const windows = [];
    
    // Add communication preference windows
    communicationTimes.forEach(timeKey => {
      const [period, hour] = timeKey.split('-');
      if (hour) {
        windows.push({
          type: 'communication',
          start: parseInt(hour),
          end: parseInt(hour) + 1,
          score: 0.8,
          reason: 'communication-preference'
        });
      }
    });

    // Add productivity-adjacent windows (not during, but near)
    productivityPeaks.forEach(period => {
      const hourRange = this.periodToHourRange(period);
      if (hourRange) {
        // Add window before productivity peak
        windows.push({
          type: 'pre-productivity',
          start: Math.max(0, hourRange.start - 1),
          end: hourRange.start,
          score: 0.6,
          reason: 'before-productivity-peak'
        });
        
        // Add window after productivity peak
        windows.push({
          type: 'post-productivity',
          start: hourRange.end,
          end: Math.min(23, hourRange.end + 1),
          score: 0.6,
          reason: 'after-productivity-peak'
        });
      }
    });

    return windows.sort((a, b) => b.score - a.score);
  }

  /**
   * Helper methods
   */
  
  isCurrentTimeOptimal(personal, temporal) {
    const communicationTimes = personal.preferredCommunicationTimes || [];
    const currentTimeKey = `${temporal.period}-${temporal.hour}`;
    return communicationTimes.includes(currentTimeKey);
  }

  assessInterruptionLevel(temporal, personal) {
    let interruption = 0.3; // Base interruption level

    // Higher interruption during productivity peaks
    if (personal.productivityPeaks?.includes(temporal.period)) {
      interruption += 0.4;
    }

    // Lower interruption during social periods
    if (temporal.focus === 'family-time' || temporal.focus === 'social') {
      interruption -= 0.2;
    }

    // Higher interruption during high energy periods
    if (temporal.energy === 'high') {
      interruption += 0.2;
    }

    return Math.max(0, Math.min(1, interruption));
  }

  getEnergyScore(energyLevel) {
    switch (energyLevel) {
      case 'high': return 0.3; // High energy = focused, don't interrupt
      case 'normal': return 0.7; // Normal energy = good for notifications
      case 'low': return 0.4; // Low energy = might be tired
      case 'rising': return 0.8; // Rising energy = good timing
      case 'winding-down': return 0.6; // Winding down = ok timing
      default: return 0.5;
    }
  }

  getPatternScore(patterns, category) {
    // Score based on historical response patterns
    const bestTimes = patterns.bestResponseTimes || [];
    const avoidPeriods = patterns.avoidancePeriods || [];
    
    // This is a simplified implementation
    return 0.6; // Would analyze patterns more deeply in full implementation
  }

  getContextScore(contextAdjustments) {
    let score = 0.5;
    
    if (contextAdjustments.familyAvailability === 'high') score += 0.2;
    if (contextAdjustments.workFocus === 'low') score += 0.2;
    if (contextAdjustments.energyAlignment === 'good') score += 0.1;
    
    return Math.max(0, Math.min(1, score));
  }

  periodToHourRange(period) {
    const ranges = {
      'morning': { start: 5, end: 9 },
      'mid-morning': { start: 9, end: 12 },
      'lunch': { start: 12, end: 14 },
      'afternoon': { start: 14, end: 17 },
      'early-evening': { start: 17, end: 19 },
      'evening': { start: 19, end: 22 },
      'late-night': { start: 22, end: 24 }
    };
    
    return ranges[period];
  }

  // Additional helper methods would be implemented here...
  generateNotificationId() {
    return 'notif_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  async getUserContext(userId) {
    // This would integrate with the context engine
    const familyData = {}; // Get from family context
    return await contextEngine.buildContext(userId, familyData);
  }

  emitNotification(notification) {
    // This would integrate with your notification system
    // For now, just log it
    console.log('📱 Notification:', notification.title);
  }

  // Placeholder implementations for remaining methods
  isInQuietHours() { return false; }
  getNextAvailableTime(userId, now, quietHours) { return addMinutes(now, 30); }
  hasRecentSimilarNotification() { return false; }
  getNextAllowedTime(userId, category, minInterval) { return addMinutes(new Date(), minInterval); }
  findNextGoodTiming(optimalTiming, priority) { return addMinutes(new Date(), 60); }
  getQuietHours() { return { start: 22, end: 7 }; }
  getBestResponseTimes() { return []; }
  getAvoidancePeriods() { return []; }
  getOptimalNotificationGaps() { return 30; }
  assessFamilyAvailability() { return 'medium'; }
  assessWorkFocusPeriod() { return 'medium'; }
  assessEnergyAlignment() { return 'good'; }
  rescheduleNotification(notification, newTime, reason) {
    notification.scheduledAt = newTime;
    notification.rescheduleReason = reason;
  }
  suppressNotification(notification, reason) {
    notification.status = 'suppressed';
    notification.suppressReason = reason;
  }
}

// Export singleton instance
export const notificationTimingService = new NotificationTimingService();
export default notificationTimingService;